/**
 * 🔒 POLITIQUE DE CONFIDENTIALITÉ - Heartbeats
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Lock, Database, Eye, Trash2, Share2, Globe, Shield } from 'lucide-react';

const HeartbeatsConfidentialite = ({ onBack }) => {
  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] text-white" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={onBack || (() => window.history.back())}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" fill="currentColor" />
            <h1 className="text-xl font-bold">Politique de Confidentialité</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Introduction */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-pink-400 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Notre engagement
            </h2>
            <div className="space-y-3 text-white/80">
              <p>
                Chez Heartbeats, nous prenons la protection de vos données personnelles 
                très au sérieux. Cette politique de confidentialité explique comment nous 
                collectons, utilisons et protégeons vos informations.
              </p>
              <p>
                Nous sommes conformes au Règlement Général sur la Protection des Données 
                (RGPD) et aux lois applicables en matière de protection des données.
              </p>
            </div>
          </section>

          {/* Données collectées */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Données collectées
            </h2>
            <div className="space-y-4 text-white/80">
              <div>
                <p className="font-semibold text-white mb-2">Données d'inscription</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Adresse email</li>
                  <li>Prénom</li>
                  <li>Date de naissance (pour vérifier la majorité)</li>
                  <li>Mot de passe (stocké de manière chiffrée)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white mb-2">Données de profil</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Photos de profil</li>
                  <li>Description / biographie</li>
                  <li>Genre et préférences de rencontre</li>
                  <li>Centres d'intérêt / passions</li>
                  <li>Localisation (ville, code postal)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white mb-2">Données d'utilisation</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Historique des likes et matchs</li>
                  <li>Messages échangés</li>
                  <li>Achats effectués (couronnes, abonnements)</li>
                  <li>Logs de connexion (adresse IP, appareil)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Utilisation des données */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Utilisation des données
            </h2>
            <div className="space-y-3 text-white/80">
              <p>Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Fournir le service</strong> - Création de profil, matching, messagerie</li>
                <li><strong>Personnalisation</strong> - Suggestions de profils compatibles</li>
                <li><strong>Sécurité</strong> - Détection de fraudes et comportements abusifs</li>
                <li><strong>Communication</strong> - Notifications, emails de service</li>
                <li><strong>Amélioration</strong> - Analyse anonymisée pour améliorer l'application</li>
                <li><strong>Obligations légales</strong> - Réponse aux demandes des autorités</li>
              </ul>
            </div>
          </section>

          {/* Partage des données */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Partage des données
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>Nous ne vendons JAMAIS vos données personnelles.</strong></p>
              <p>Vos données peuvent être partagées avec :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Autres utilisateurs</strong> - Uniquement les informations de votre profil public</li>
                <li><strong>Prestataires techniques</strong> - Hébergement (Emergent), paiement (Stripe), email (Resend)</li>
                <li><strong>Autorités</strong> - En cas d'obligation légale ou demande judiciaire</li>
              </ul>
              <p className="mt-4">
                Tous nos prestataires sont soumis à des accords de confidentialité stricts 
                et conformes au RGPD.
              </p>
            </div>
          </section>

          {/* Conservation */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-green-400 mb-4">Conservation des données</h2>
            <div className="space-y-3 text-white/80">
              <p>Vos données sont conservées :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Compte actif</strong> - Tant que votre compte est actif</li>
                <li><strong>Compte supprimé</strong> - 30 jours après suppression (période de récupération)</li>
                <li><strong>Données de paiement</strong> - 10 ans (obligations comptables)</li>
                <li><strong>Logs de sécurité</strong> - 1 an</li>
              </ul>
            </div>
          </section>

          {/* Droits */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Vos droits (RGPD)
            </h2>
            <div className="space-y-3 text-white/80">
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Droit d'accès</strong> - Obtenir une copie de vos données</li>
                <li><strong>Droit de rectification</strong> - Corriger des données inexactes</li>
                <li><strong>Droit à l'effacement</strong> - Supprimer vos données ("droit à l'oubli")</li>
                <li><strong>Droit à la portabilité</strong> - Recevoir vos données dans un format standard</li>
                <li><strong>Droit d'opposition</strong> - Vous opposer à certains traitements</li>
                <li><strong>Droit de limitation</strong> - Limiter le traitement de vos données</li>
              </ul>
              <p className="mt-4">
                Pour exercer ces droits : <strong>privacy@heartbeats.fr</strong>
              </p>
              <p>
                Vous pouvez également supprimer votre compte directement dans l'application 
                (Paramètres → Supprimer mon compte).
              </p>
            </div>
          </section>

          {/* Sécurité */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Sécurité des données
            </h2>
            <div className="space-y-3 text-white/80">
              <p>Nous mettons en œuvre des mesures de sécurité robustes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Mots de passe hashés avec algorithmes sécurisés</li>
                <li>Authentification à deux facteurs disponible</li>
                <li>Serveurs sécurisés avec accès restreint</li>
                <li>Audits de sécurité réguliers</li>
                <li>Formation de notre équipe à la protection des données</li>
              </ul>
            </div>
          </section>

          {/* Cookies */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-orange-400 mb-4">Cookies</h2>
            <div className="space-y-3 text-white/80">
              <p>Heartbeats utilise des cookies pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cookies essentiels</strong> - Fonctionnement de l'application, session</li>
                <li><strong>Cookies de préférences</strong> - Mémoriser vos choix (langue, thème)</li>
                <li><strong>Cookies analytiques</strong> - Comprendre l'utilisation (anonymisé)</li>
              </ul>
              <p className="mt-4">
                Nous n'utilisons PAS de cookies publicitaires ni de tracking tiers.
              </p>
            </div>
          </section>

          {/* International */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-indigo-400 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Transferts internationaux
            </h2>
            <div className="space-y-3 text-white/80">
              <p>
                Vos données sont principalement stockées dans l'Union Européenne.
              </p>
              <p>
                En cas de transfert hors UE, nous nous assurons que les garanties appropriées 
                sont en place (clauses contractuelles types, pays avec décision d'adéquation).
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-pink-400 mb-4">Contact DPO</h2>
            <div className="space-y-3 text-white/80">
              <p>Pour toute question relative à vos données personnelles :</p>
              <p>📧 Email : <strong>privacy@heartbeats.fr</strong></p>
              <p>📍 Adresse : Heartbeats - DPO, 141 Rue Arthur Lamendin, 62980 Vermelles, France</p>
              <p className="mt-4">
                Vous avez également le droit d'introduire une réclamation auprès de la CNIL 
                (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr
              </p>
            </div>
          </section>

          {/* Date */}
          <p className="text-center text-white/40 text-sm">
            Dernière mise à jour : Janvier 2026
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default HeartbeatsConfidentialite;
